import { tariffCatalogPreviewInputSchema } from "@ai-crm/shared";
import type { PaidTariffDTO, TariffCatalogDTO, TariffCatalogPreviewDTO, TariffDefinition } from "@ai-crm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { PageHeader } from "../components/layout";
import { adminApi, ApiError } from "../lib/api";
import { useAdminAuth } from "../lib/auth";
import { formatMoscowDateTime, formatRubTotal } from "../lib/format";

const buttonClass = "rounded-md border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50";
const primaryButtonClass = `${buttonClass} border-primary bg-primary text-primary-foreground`;
const queryKey = ["admin", "tariffs"] as const;

function trialConditions(trial: TariffDefinition["trial"]): string {
  return trial.settings
    ? `${trial.settings.durationDays} дней, ${trial.settings.totalDialogs} диалогов на весь срок`
    : "Не настроен";
}

export function TariffsPage(): React.JSX.Element {
  const { token } = useAdminAuth();
  const client = useQueryClient();
  const catalog = useQuery({
    queryKey,
    queryFn: () => adminApi.tariffs(token!),
    enabled: Boolean(token),
  });
  const [draft, setDraft] = useState<TariffDefinition | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [baseTrial, setBaseTrial] = useState<TariffDefinition["trial"] | null>(null);
  const [trialDurationDays, setTrialDurationDays] = useState("");
  const [trialTotalDialogs, setTrialTotalDialogs] = useState("");
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState<TariffCatalogPreviewDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const confirmInFlight = useRef(false);

  useEffect(() => {
    if (catalog.data && !dirty && !preview) {
      setDraft(structuredClone(catalog.data.definition));
      setBaseVersion(catalog.data.version);
      setBaseTrial(structuredClone(catalog.data.definition.trial));
      setTrialDurationDays(catalog.data.definition.trial.settings?.durationDays.toString() ?? "");
      setTrialTotalDialogs(catalog.data.definition.trial.settings?.totalDialogs.toString() ?? "");
    }
  }, [catalog.data, dirty, preview]);

  function changeDraft(next: TariffDefinition) {
    setDraft(next);
    setDirty(true);
    setPreview(null);
    setError(null);
  }

  function changePaid(index: number, patch: Partial<PaidTariffDTO>) {
    if (!draft) return;
    changeDraft({
      ...draft,
      paidTariffs: draft.paidTariffs.map((tariff, position) =>
        position === index ? { ...tariff, ...patch } : tariff,
      ),
    });
  }

  async function savePreview() {
    if (!token || !draft || baseVersion === null || busy) return;
    const leaveTrialUnconfigured = draft.trial.settings === null && baseTrial?.settings === null && trialDurationDays === "" && trialTotalDialogs === "";
    if (!leaveTrialUnconfigured && (trialDurationDays === "" || trialTotalDialogs === "")) {
      setError("Для настройки пробного периода заполните длительность и общий запас диалогов.");
      return;
    }
    const definition: TariffDefinition = {
      ...draft,
      trial: {
        ...draft.trial,
        settings: leaveTrialUnconfigured ? null : {
          durationDays: Number(trialDurationDays),
          totalDialogs: Number(trialTotalDialogs),
        },
      },
    };
    const parsed = tariffCatalogPreviewInputSchema.safeParse({ expectedVersion: baseVersion, definition });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setError(`Проверьте тарифы: ${issue?.path.join(".") ?? "definition"} — ${issue?.message ?? "ошибка ввода"}`);
      return;
    }
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const result = await adminApi.previewTariffs(token, parsed.data);
      setPreview(result);
      setDraft(structuredClone(parsed.data.definition));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setConflict(true);
        setError("Каталог изменился. Загрузите актуальную версию и повторите предпросмотр.");
      } else {
        setError("Не удалось получить предпросмотр. Изменения остались в форме.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function reloadCurrent() {
    if (!token || busy) return;
    setBusy(true);
    try {
      const current = await adminApi.tariffs(token);
      client.setQueryData(queryKey, current);
      setBaseVersion(current.version);
      setBaseTrial(structuredClone(current.definition.trial));
      setConflict(false);
      setError(null);
      setPreview(null);
      setDirty(true);
    } catch {
      setError("Не удалось загрузить актуальную версию. Черновик сохранён в форме.");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!token || !preview || busy || confirmInFlight.current) return;
    confirmInFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await adminApi.confirmTariffs(token, {
        expectedVersion: preview.expectedVersion,
        previewToken: preview.previewToken,
      });
      client.setQueryData<TariffCatalogDTO>(queryKey, result);
      setDraft(structuredClone(result.definition));
      setBaseVersion(result.version);
      setBaseTrial(structuredClone(result.definition.trial));
      setTrialDurationDays(result.definition.trial.settings?.durationDays.toString() ?? "");
      setTrialTotalDialogs(result.definition.trial.settings?.totalDialogs.toString() ?? "");
      setDirty(false);
      setPreview(null);
      setConflict(false);
      await Promise.all([
        client.invalidateQueries({ queryKey: ["admin", "accounts"] }),
        client.invalidateQueries({ queryKey: ["admin", "account"] }),
      ]);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setPreview(null);
        if (caught.code === "BILLING_PREVIEW_EXPIRED") {
          setError("Срок предпросмотра истёк. Повторите предпросмотр перед подтверждением.");
        } else {
          setConflict(true);
          setError("Каталог изменился. Загрузите актуальную версию и повторите предпросмотр.");
        }
      } else {
        setError("Не удалось подтвердить каталог. Можно повторить подтверждение показанного предпросмотра.");
      }
    } finally {
      confirmInFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Тарифы" description="Изменения вступают в силу после подтверждения серверного предпросмотра." />
      {catalog.isPending && <p role="status">Загружаем каталог тарифов...</p>}
      {catalog.isError && !catalog.data && (
        <div role="alert" className="panel p-4 text-danger">
          Не удалось загрузить каталог. <button className={buttonClass} type="button" onClick={() => void catalog.refetch()}>Повторить</button>
        </div>
      )}
      {draft && baseVersion !== null && (
        <>
          <p className="text-sm text-muted-foreground">Актуальная версия: {baseVersion}{dirty ? " · Локальный черновик" : ""}</p>
          {error && <div role="alert" className="panel p-4 text-sm text-danger">{error}</div>}
          {conflict && <button className={buttonClass} type="button" disabled={busy} onClick={() => void reloadCurrent()}>Загрузить актуальную версию</button>}
          {preview ? (
            <section className="panel space-y-4 p-4" aria-label="Предпросмотр каталога">
              <h2 className="text-lg font-semibold">Предпросмотр версии {preview.catalog.version}</h2>
              <p className="text-sm text-muted-foreground">Действует до {formatMoscowDateTime(preview.expiresAt)} (МСК). До подтверждения действует версия {preview.expectedVersion}.</p>
              <div className="space-y-1 text-sm">
                <p>Freemium: {preview.catalog.definition.freemium.name} — {preview.catalog.definition.freemium.monthlyDialogs} диалогов за 30 дней.</p>
                <div className="space-y-1 rounded-md border p-3" aria-label="Условия пробного периода">
                  <h3 className="font-semibold">Пробный период: {preview.diff.trialChanged ? "изменён" : "без изменений"}</h3>
                  {baseTrial && <p>Было: {baseTrial.name} — {trialConditions(baseTrial)}.</p>}
                  <p>Будет: {preview.catalog.definition.trial.name} — {trialConditions(preview.catalog.definition.trial)}.</p>
                  <p className="text-muted-foreground">Условия изменятся только для новых активаций.</p>
                </div>
                {preview.catalog.definition.paidTariffs.map((tariff) => (
                  <p key={tariff.id} className="break-words">{tariff.name} ({tariff.id}): {tariff.monthlyDialogs} диалогов, {tariff.unitPriceRub} ₽ за диалог.</p>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-left text-sm">
                  <thead><tr className="border-b"><th className="p-2">Тариф</th><th className="p-2">База за месяц</th><th className="p-2">Срок</th><th className="p-2">Без скидки</th><th className="p-2">Скидка</th><th className="p-2">Экономия</th><th className="p-2">Итого</th></tr></thead>
                  <tbody>{preview.catalog.baseQuotes.map((quote) => (
                    <tr className="border-b" key={`${quote.tariffId}-${quote.months}`}>
                      <td className="p-2 break-words">{quote.tariffName}</td>
                      <td className="p-2">{quote.breakdown.baseMonthlyRub} ₽</td>
                      <td className="p-2">{quote.months} мес. × 30 дней = {quote.months * quote.periodDays} дней</td>
                      <td className="p-2">{quote.breakdown.undiscountedRub} ₽</td>
                      <td className="p-2">{quote.discountPercent}%</td>
                      <td className="p-2">{quote.breakdown.discountRub} ₽</td>
                      <td className="p-2 font-semibold">{formatRubTotal(quote.breakdown.totalRub)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <p className="text-sm">Добавлены: {preview.diff.addedTariffIds.join(", ") || "нет"}; удалены: {preview.diff.removedTariffIds.join(", ") || "нет"}; изменены: {preview.diff.changedTariffIds.join(", ") || "нет"}; Freemium: {preview.diff.freemiumChanged ? "изменён" : "без изменений"}; пробный период: {preview.diff.trialChanged ? "изменён" : "без изменений"}.</p>
              <p className="text-sm text-muted-foreground">Новый лимит Freemium действует сразу после подтверждения без сброса использования и дат. Условия оплаченных подписок сохраняются до окончания оплаченного срока.</p>
              <div className="flex flex-wrap gap-2">
                <button className={buttonClass} type="button" disabled={busy} onClick={() => setPreview(null)}>Назад</button>
                <button className={primaryButtonClass} type="button" disabled={busy} onClick={() => void confirm()}>Подтвердить</button>
              </div>
            </section>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
              <section className="panel space-y-3 p-4">
                <h2 className="text-lg font-semibold">Freemium</h2>
                <p className="text-xs text-muted-foreground">ID: freemium · 30 дней · бесплатно</p>
                <label className="block space-y-1 text-sm"><span>Название Freemium</span><input className="control" value={draft.freemium.name} disabled={busy} onChange={(event) => changeDraft({ ...draft, freemium: { ...draft.freemium, name: event.target.value } })} /></label>
                <label className="block space-y-1 text-sm"><span>Месячный объём: Freemium</span><input className="control" type="number" min="0" value={draft.freemium.monthlyDialogs} disabled={busy} onChange={(event) => changeDraft({ ...draft, freemium: { ...draft.freemium, monthlyDialogs: Number(event.target.value) } })} /></label>
              </section>
              <section className="panel space-y-3 p-4" aria-label="Пробный период">
                <h2 className="text-lg font-semibold">Пробный период</h2>
                <p className="text-xs text-muted-foreground">ID: trial · бесплатно · один раз на аккаунт</p>
                <p className="text-sm">Все функции платных тарифов. Общий запас диалогов на весь срок, без обновления каждые 30 дней.</p>
                {draft.trial.settings === null && <p className="text-sm text-muted-foreground">Не настроен. Для включения заполните оба числовых поля и подтвердите условия.</p>}
                <label className="block space-y-1 text-sm"><span>Название пробного периода</span><input className="control" value={draft.trial.name} disabled={busy} onChange={(event) => changeDraft({ ...draft, trial: { ...draft.trial, name: event.target.value } })} /></label>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block space-y-1 text-sm"><span>Длительность пробного периода, дней</span><input className="control" type="number" min="1" max="2000000" step="1" value={trialDurationDays} disabled={busy} onChange={(event) => { setTrialDurationDays(event.target.value); changeDraft(draft); }} /></label>
                  <label className="block space-y-1 text-sm"><span>Общий запас диалогов пробного периода</span><input className="control" type="number" min="1" max="2147483647" step="1" value={trialTotalDialogs} disabled={busy} onChange={(event) => { setTrialTotalDialogs(event.target.value); changeDraft(draft); }} /></label>
                </div>
                <p className="text-sm text-muted-foreground">Условия изменятся только для новых активаций.</p>
              </section>
              </div>
              {draft.paidTariffs.map((tariff, index) => (
                <section className="panel space-y-3 p-4" key={tariff.id}>
                  <div className="flex items-start justify-between gap-3"><h2 className="min-w-0 break-words text-lg font-semibold">{tariff.name || "Новый тариф"}</h2><button className={buttonClass} type="button" disabled={busy} onClick={() => changeDraft({ ...draft, paidTariffs: draft.paidTariffs.filter((_, position) => position !== index) })}>Удалить {tariff.name || tariff.id}</button></div>
                  <label className="block space-y-1 text-sm"><span>ID тарифа: {tariff.name}</span><input className="control" value={tariff.id} readOnly aria-readonly="true" /></label>
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="block space-y-1 text-sm"><span>Название тарифа: {tariff.id}</span><input className="control" value={tariff.name} disabled={busy} onChange={(event) => changePaid(index, { name: event.target.value })} /></label>
                    <label className="block space-y-1 text-sm"><span>Месячный объём: {tariff.name}</span><input className="control" type="number" min="1" value={tariff.monthlyDialogs} disabled={busy} onChange={(event) => changePaid(index, { monthlyDialogs: Number(event.target.value) })} /></label>
                    <label className="block space-y-1 text-sm"><span>Цена за диалог: {tariff.name}</span><input className="control" inputMode="decimal" value={tariff.unitPriceRub} disabled={busy} onChange={(event) => changePaid(index, { unitPriceRub: event.target.value })} /></label>
                  </div>
                  <h3 className="font-medium">Сроки и скидки</h3>
                  {tariff.terms.map((term, termIndex) => (
                    <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]" key={termIndex}>
                      <label className="block space-y-1 text-sm"><span>Месяцев: {tariff.name}, срок {termIndex + 1}</span><input className="control" type="number" min="1" value={term.months} disabled={busy} onChange={(event) => changePaid(index, { terms: tariff.terms.map((item, position) => position === termIndex ? { ...item, months: Number(event.target.value) } : item) })} /></label>
                      <label className="block space-y-1 text-sm"><span>Скидка %: {tariff.name}, срок {termIndex + 1}</span><input className="control" inputMode="decimal" value={term.discountPercent} disabled={busy} onChange={(event) => changePaid(index, { terms: tariff.terms.map((item, position) => position === termIndex ? { ...item, discountPercent: event.target.value } : item) })} /></label>
                      <button className={buttonClass} type="button" disabled={busy} onClick={() => changePaid(index, { terms: tariff.terms.filter((_, position) => position !== termIndex) })}>Удалить срок {termIndex + 1}</button>
                    </div>
                  ))}
                  <button className={buttonClass} type="button" disabled={busy} onClick={() => changePaid(index, { terms: [...tariff.terms, { months: 1, discountPercent: "0" }] })}>Добавить срок</button>
                </section>
              ))}
              <div className="flex flex-wrap gap-2">
                <button className={buttonClass} type="button" disabled={busy} onClick={() => changeDraft({ ...draft, paidTariffs: [...draft.paidTariffs, { id: crypto.randomUUID(), name: "", monthlyDialogs: 1, unitPriceRub: "1", terms: [{ months: 1, discountPercent: "0" }] }] })}>Добавить тариф</button>
                <button className={primaryButtonClass} type="button" disabled={busy || conflict} onClick={() => void savePreview()}>Сохранить</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
